import { DesktopDatePicker, DesktopDatePickerProps, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { TextField, TextFieldProps } from '@mui/material';
import { format, parse } from 'date-fns';
import React, { useCallback, useMemo } from 'react';

import { RowFieldBase } from '../row-field-base';

type Props = Omit<DesktopDatePickerProps<Date>, 'renderInput' | 'onChange' | 'value'> &
  Pick<TextFieldProps, 'id' | 'name' | 'error' | 'helperText'> & {
    value: string | null;
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    onChange: (date: string | 'invalid-date' | null) => void;
    isRow?: boolean;
  };

const FALLBACK_MIN_DATE = new Date(1900, 0, 1);
const FALLBACK_MAX_DATE = new Date(2099, 11, 31);

const DateField: React.FC<Props> = ({
  id,
  name,
  error,
  helperText,
  isRow,
  // date picker
  label,
  value,
  onChange,
  ...datePickerProps
}: Props) => {
  const handleChange = useCallback(
    (date: Date | null) => {
      if (date === null) {
        onChange(date);
      } else if (isNaN(date.getDate())) {
        onChange('invalid-date');
      } else {
        const fullYear = date.getFullYear();
        if (fullYear < 1000) {
          const twoDigitYearDate = parse(format(date, 'yy-MM-dd'), 'yy-MM-dd', new Date(2000, 0, 1));
          onChange(format(twoDigitYearDate, 'yyyy-MM-dd'));
        } else {
          onChange(format(date, 'yyyy-MM-dd'));
        }
      }
    },
    [onChange],
  );
  const dateValue = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;

  const renderInput = useMemo(() => {
    const extraProps: Pick<TextFieldProps, 'size' | 'margin'> = isRow ? { size: 'small', margin: 'none' } : {};
    return function Input(props: TextFieldProps) {
      props = {
        ...props,
        inputProps: { ...props.inputProps, type: 'text', inputMode: 'numeric' },
      };
      return (
        <TextField
          {...props}
          id={id}
          name={name}
          error={error}
          helperText={helperText}
          fullWidth={true}
          {...extraProps}
        />
      );
    };
  }, [error, helperText, id, isRow, name]);

  const pickerProps: DesktopDatePickerProps<Date> = {
    value: dateValue,
    onChange: handleChange,
    renderInput,
    minDate: FALLBACK_MIN_DATE,
    maxDate: FALLBACK_MAX_DATE,
    ...datePickerProps,
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isRow ? (
        <RowFieldBase id={id} label={label}>
          <DesktopDatePicker {...pickerProps} />
        </RowFieldBase>
      ) : (
        <DesktopDatePicker label={label} {...pickerProps} />
      )}
    </LocalizationProvider>
  );
};

export { DateField };
